/**
 * @studox/auth — the single source of truth for identity, ranks & permissions
 * across the ENTIRE StudoX ecosystem (core, flow, create, cinema, clubs, lyrics,
 * help, invest). Pure, framework-agnostic ESM. React bindings live in ./react.jsx,
 * the browser SSO bridge in ./sso.js, and a CommonJS twin for the backend in
 * ./node.cjs.
 *
 * Design law: rank ids === backend `User.role` strings. We NEVER rename backend
 * roles, so adopting this package can never lock anyone out.
 */
// Vite/Rollup import JSON natively; no import-assertion needed (keeps bundlers happy).
import RANKS from "./ranks.json";

export const ALL_APPS = RANKS.apps;
export const CAPABILITY_LABELS = RANKS.capabilityLabels;
export const RANK_ORDER = Object.entries(RANKS.ranks)
  .sort((a, b) => b[1].level - a[1].level)
  .map(([id]) => id);

const FALLBACK = { id: "student", ...RANKS.ranks.student };

/** Accepts a role string OR a user object → returns the rank definition (with id). */
export function rankOf(roleOrUser) {
  const role = typeof roleOrUser === "string" ? roleOrUser : roleOrUser?.role;
  const def = role && RANKS.ranks[role];
  return def ? { id: role, ...def } : FALLBACK;
}

export function rankMeta(roleId) {
  const def = RANKS.ranks[roleId];
  return def ? { id: roleId, ...def } : null;
}

/** Numeric power level of a role (higher = more powerful). */
export function levelOf(roleOrUser) {
  return rankOf(roleOrUser).level;
}

/** Does the user have a capability? Overseer wildcard "*" grants everything. */
export function hasCapability(roleOrUser, capability) {
  const caps = rankOf(roleOrUser).capabilities || [];
  return caps.includes("*") || caps.includes(capability);
}

/** Is the user's rank at least `minRoleId` (by level)? */
export function rankAtLeast(roleOrUser, minRoleId) {
  return levelOf(roleOrUser) >= (rankMeta(minRoleId)?.level ?? Infinity);
}

/** Is the user staff (teacher level and up)? */
export function isStaff(roleOrUser) {
  return levelOf(roleOrUser) >= RANKS.ranks.teacher.level;
}

/** Can an actor of `actorRole` create an account/code of `targetRole`?
 *  This enforces the core rule: ONLY Overseer & SuperAdmin create accounts. */
export function canCreateRank(actorRoleOrUser, targetRole) {
  const actor = rankOf(actorRoleOrUser);
  if (actor.capabilities?.includes("*")) return true;
  return Array.isArray(actor.canCreate) && actor.canCreate.includes(targetRole);
}

/** The full list of ranks an actor may mint codes/accounts for. */
export function creatableRanks(actorRoleOrUser) {
  const actor = rankOf(actorRoleOrUser);
  if (actor.capabilities?.includes("*")) return RANK_ORDER.filter((r) => r !== "solid_ground");
  return [...(actor.canCreate || [])];
}

/** Parse a user's allowedApps (JSON string OR array) safely. */
export function parseAllowedApps(user) {
  const raw = user?.allowedApps;
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw || '["flow"]'); } catch { return ["flow"]; }
}

/** Which apps may this user open? Platform admins (apps: "*") get everything;
 *  otherwise we honor the backend `allowedApps` gate (source of truth stays the DB). */
export function appAccess(user) {
  const rank = rankOf(user);
  if (rank.apps === "*") return [...ALL_APPS];
  const allowed = parseAllowedApps(user);
  // union of rank defaults that are also granted — but never less than allowedApps
  return Array.from(new Set([...allowed]));
}

export function canOpenApp(user, appId) {
  if (rankOf(user).apps === "*") return true;
  return appAccess(user).includes(appId);
}

/** Which Flow UI variant should this user see? */
export function flowView(roleOrUser) {
  return rankOf(roleOrUser).flowView || "student";
}

/**
 * The canonical identity object — what every page uses to know "who is this".
 * Pass any StudoX user (from /auth/me, SSO payload, or store).
 */
export function normalizeIdentity(user) {
  if (!user) return null;
  const rank = rankOf(user);
  return {
    id: user.id || user.sub || null,
    name: user.displayName || user.name || "Unbekannt",
    realName: user.name || null,
    role: rank.id,
    rank: {
      id: rank.id,
      codename: rank.codename,
      title: rank.title,
      level: rank.level,
      glyph: rank.glyph,
      color: rank.color,
      color2: rank.color2,
      flowView: rank.flowView,
    },
    avatar: user.avatarUrl || user.avatar || rank.glyph || "🦊",
    verified: !!user.isVerified || isStaff(user),
    institutionId: user.institutionId || null,
    flowView: rank.flowView,
    isStaff: isStaff(user),
  };
}

/** Human one-liner for "who you are talking to" badges. */
export function describeWho(user) {
  const idn = normalizeIdentity(user);
  if (!idn) return "Unbekannt";
  return `${idn.name} · ${idn.rank.title}${idn.verified ? " · verifiziert" : ""}`;
}

export { RANKS };
export default {
  ALL_APPS, RANK_ORDER, CAPABILITY_LABELS,
  rankOf, rankMeta, levelOf, hasCapability, rankAtLeast, isStaff,
  canCreateRank, creatableRanks, parseAllowedApps, appAccess, canOpenApp,
  flowView, normalizeIdentity, describeWho, RANKS,
};
